import dayjs from 'dayjs';
import 'dayjs/locale/fr'; // Import French locale
import duration from 'dayjs/plugin/duration';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(isToday);
dayjs.extend(isYesterday);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('fr');

const WEEKDAY_INDEX = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6
};

const windowsToIanaMap = {
  UTC: 'UTC',
  'Dateline Standard Time': 'Etc/GMT+12',
  'UTC-11': 'Etc/GMT+11',
  'Hawaiian Standard Time': 'Pacific/Honolulu',
  'Alaskan Standard Time': 'America/Anchorage',
  'Pacific Standard Time': 'America/Los_Angeles',
  'Pacific Standard Time (Mexico)': 'America/Tijuana',
  'Mountain Standard Time': 'America/Denver',
  'US Mountain Standard Time': 'America/Phoenix',
  'Central Standard Time': 'America/Chicago',
  'Central Standard Time (Mexico)': 'America/Mexico_City',
  'Eastern Standard Time': 'America/New_York',
  'US Eastern Standard Time': 'America/Indiana/Indianapolis',
  'Atlantic Standard Time': 'America/Halifax',
  'Newfoundland Standard Time': 'America/St_Johns',
  'SA Pacific Standard Time': 'America/Bogota',
  'Argentina Standard Time': 'America/Argentina/Buenos_Aires',
  'Greenwich Standard Time': 'Atlantic/Reykjavik',
  'GMT Standard Time': 'Europe/London',
  'W. Europe Standard Time': 'Europe/Berlin',
  'Romance Standard Time': 'Europe/Paris',
  'Central Europe Standard Time': 'Europe/Budapest',
  'Central European Standard Time': 'Europe/Warsaw',
  'South Africa Standard Time': 'Africa/Johannesburg',
  'Israel Standard Time': 'Asia/Jerusalem',
  'Arabic Standard Time': 'Asia/Baghdad',
  'Turkey Standard Time': 'Europe/Istanbul',
  'Russian Standard Time': 'Europe/Moscow',
  'Iran Standard Time': 'Asia/Tehran',
  'Arabian Standard Time': 'Asia/Dubai',
  'Pakistan Standard Time': 'Asia/Karachi',
  'India Standard Time': 'Asia/Kolkata',
  'Nepal Standard Time': 'Asia/Kathmandu',
  'Bangladesh Standard Time': 'Asia/Dhaka',
  'Myanmar Standard Time': 'Asia/Yangon',
  'SE Asia Standard Time': 'Asia/Bangkok',
  'China Standard Time': 'Asia/Shanghai',
  'Singapore Standard Time': 'Asia/Singapore',
  'Tokyo Standard Time': 'Asia/Tokyo',
  'Korea Standard Time': 'Asia/Seoul',
  'AUS Eastern Standard Time': 'Australia/Sydney',
  'New Zealand Standard Time': 'Pacific/Auckland',
  'Tonga Standard Time': 'Pacific/Tongatapu',
  'Line Islands Standard Time': 'Pacific/Kiritimati'
};

const ianaToWindowsMap = Object.entries(windowsToIanaMap).reduce((acc, [windows, iana]) => {
  if (!acc[iana]) {
    acc[iana] = windows; // first match wins
  }
  return acc;
}, {});

const getNextExecution = schedule => {
  try {
    const {days = [], times = [], windowsTimeZoneId} = schedule;
    if (!days.length || !times.length) return null;

    const ianaTimezone = windowsToIanaMap[windowsTimeZoneId];

    const now = dayjs().tz(ianaTimezone);
    const allowedDays = days.map(d => WEEKDAY_INDEX[d]);

    const candidates = [];

    for (let i = 0; i < 7; i++) {
      const baseDay = now.add(i, 'day');
      const weekday = baseDay.day(); // 0–6, locale-independent

      // eslint-disable-next-line no-continue
      if (!allowedDays.includes(weekday)) continue;

      times.forEach(time => {
        const [h, m] = time.split(':');

        const candidate = baseDay.hour(Number(h)).minute(Number(m)).second(0);

        if (candidate.isAfter(now)) {
          candidates.push(candidate);
        }
      });
    }

    return candidates.sort((a, b) => a.valueOf() - b.valueOf())[0] ?? null;
  } catch (e) {
    console.log({e});
    return null;
  }
};

const formatNextExecution = date => {
  const today = dayjs().startOf('day');
  const target = date.startOf('day');

  if (target.isSame(today)) {
    return `Aujourd’hui à ${date.format('HH:mm')}`;
  }

  if (target.isSame(today.add(1, 'day'))) {
    return `Demain à ${date.format('HH:mm')}`;
  }

  const formattedDay = date.format('dddd');
  const weekday = `${formattedDay.slice(0, 1).toUpperCase()}${formattedDay.slice(1)}`;
  const hour = date.format('HH:mm');

  return `${weekday} à ${hour}`;
};

const getHumanReadableSiren = siren => {
  const firstPart = siren.substring(0, 3);
  const secondPart = siren.substring(3, 6);
  const lastPart = siren.substring(6, 9);

  return `${firstPart} ${secondPart} ${lastPart}`;
};

const getHumanReadableSiret = siret => {
  const firstPart = siret.substring(0, 3);
  const secondPart = siret.substring(3, 6);
  const thirdPart = siret.substring(6, 9);
  const lastPart = siret.substring(9, 14);

  return `${firstPart} ${secondPart} ${thirdPart} ${lastPart}`;
};

const getHumanReadablePhoneNumber = phone => {
  const cleanedNumber = phone.replace(/\D/g, '');

  if (cleanedNumber === '') {
    return phone;
  }

  let formattedNumber = '';
  for (let i = 0; i < cleanedNumber.length; i += 2) {
    const isPhoneNumberEven = i + 2 <= cleanedNumber.length;
    const digitsToAdd = isPhoneNumberEven ? `${cleanedNumber.substring(i, i + 2)} ` : cleanedNumber.substring(i);
    formattedNumber += digitsToAdd;
  }

  return formattedNumber.trim();
};

const getTruncatedString = (string, maxLength = 20) => (string.length <= maxLength ? string : `${string.substring(0, maxLength)}...`);

const isProjectDemo = siren => {
  const demoSirenRegex = /^10000000\d[_\d\w-]*$/;
  return demoSirenRegex.test(siren);
};

const generateUniqueIdFromProjectName = str => {
  const charCodes = str.split('').map(char => char.charCodeAt(0));

  let hash = 0;
  for (let i = 0; i < charCodes.length; i++) {
    hash = (hash << 5) - hash + charCodes[i];
    hash |= 0;
  }

  return hash.toString();
};

const makeHumanFriendlyUrl = url => {
  let friendlyUrl = url;

  friendlyUrl = friendlyUrl.replace(/%20/g, '-');
  friendlyUrl = friendlyUrl.replace(/ /g, '-');
  friendlyUrl = friendlyUrl.toLowerCase();

  return friendlyUrl;
};

const getFrenchFormattedDate = date => {
  const options = {
    day: '2-digit',
    month: date instanceof Date ? '2-digit' : 'long',
    year: 'numeric'
  };
  if (date instanceof Date) {
    return date.toLocaleDateString('fr-FR', options);
  }
  const [day, month, year] = date.split('/');

  return new Date([month, day, year].join('/')).toLocaleDateString('fr-FR', options);
};

const getInternationalStandardDateFormat = dateInput => {
  let isoFormattedDate;

  if (typeof dateInput === 'string' && dateInput.includes('/')) {
    // If the input is a string and includes '/', assume it's in DD/MM/YYYY format
    const [day, month, year] = dateInput.split('/');
    [isoFormattedDate] = new Date(`${year}-${month}-${day}T00:00:00.000Z`).toISOString().split('T');
  } else {
    // If the input is not a string or doesn't include '/', assume it's in another format
    const inputDate = new Date(dateInput);
    [isoFormattedDate] = inputDate.toISOString().split('T');
  }

  return isoFormattedDate;
};

const isFrenchDateFormat = inputString => {
  const dateFormats = [/^\d{2}\/\d{2}\/\d{4}$/, /^\w{3} \w{3} \d{2} \d{4} \d{2}:\d{2}:\d{2} \w{3}\s\(\w+ \w+ \d{4}\)$/];

  const dateString = String(inputString);

  // eslint-disable-next-line no-restricted-syntax
  for (const dateFormat of dateFormats) {
    const match = dateString.match(dateFormat);
    if (match) {
      return true;
    }
  }

  return false;
};

const createDateObjectFromFrenchFormattedDate = dateString => {
  if (isFrenchDateFormat(dateString)) {
    const parts = dateString.split('/');
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Months are 0-indexed in JavaScript
    const year = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }

  return new Date(dateString);
};

const formatFrenchDateVerboseFromTimeStamp = timestamp => {
  dayjs.locale('fr'); // Set locale to French

  const date = dayjs.unix(timestamp); // Convert Unix timestamp to dayjs object
  return date.format('dddd D MMMM YYYY'); // Format the date according to your requirement
};

const getRoundedPercentage = (ratio, numberOfDecimals = 3) => {
  const roundedRatio = Number(ratio.toFixed(numberOfDecimals));
  const roundedPercentage = Number((roundedRatio * 100).toFixed(numberOfDecimals));
  return roundedPercentage;
};

const isTokenExpired = token => {
  return Date.now() > token.exp * 1000;
};

const getLastDayOfPreviousMonthFrenchFormatted = () => {
  const lastDayOfPreviousMonth = dayjs().subtract(1, 'month').endOf('month').format('DD/MM/YYYY');
  return lastDayOfPreviousMonth;
};

const formatJsonSummaryAccountingDataToCsv = jsonData => {
  const columns = Object.keys(jsonData[0]);

  const csvData = [
    columns.join(','), // Header row
    ...jsonData.map(row =>
      columns
        .map(col => {
          return `"${row[col]}"`;
        })
        .join(',')
    )
  ].join('\n');

  return csvData;
};

/**
 * Normalises value to be used by MUI Progress component (range 0-100)
 * @param value
 * @param maxValue
 * @returns {number}
 */
const normaliseValue = (value, maxValue) => {
  const minValue = 0;
  if (value <= minValue) {
    return 0;
  }
  if (value >= maxValue) {
    return 100;
  }
  return ((value - minValue) / (maxValue - minValue)) * 100;
};

const formatDuration = (start, end) => {
  const d = dayjs.duration(dayjs(end).diff(dayjs(start)));

  const h = d.hours();
  const m = d.minutes();
  const s = d.seconds();

  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const getHumanReadableTimeElapsedFromDate = date => {
  const userTz = dayjs.tz.guess();

  const d = dayjs.utc(date).tz(userTz);
  const now = dayjs().tz(userTz);

  if (!d.isValid()) return '';

  const diffSeconds = now.diff(d, 'second');
  const diffMinutes = now.diff(d, 'minute');
  const diffHours = now.diff(d, 'hour');

  if (diffSeconds < 60) {
    return 'il y a quelques secondes';
  }

  if (diffMinutes < 60) {
    return `il y a ${diffMinutes} mn`;
  }

  if (diffHours < 4) {
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    if (minutes === 0) {
      return `il y a ${hours}h`;
    }

    return `il y a ${hours}h ${minutes}mn`;
  }

  if (d.isToday()) {
    return `Aujourd’hui à ${d.format('HH:mm')}`;
  }

  if (d.isYesterday()) {
    return `Hier à ${d.format('HH:mm')}`;
  }

  return `Le ${d.format('DD/MM/YYYY')} à ${d.format('HH:mm')}`;
};

export {
  getHumanReadableSiren,
  getHumanReadableSiret,
  getTruncatedString,
  isProjectDemo,
  getHumanReadablePhoneNumber,
  generateUniqueIdFromProjectName,
  getFrenchFormattedDate,
  getInternationalStandardDateFormat,
  makeHumanFriendlyUrl,
  createDateObjectFromFrenchFormattedDate,
  getRoundedPercentage,
  isTokenExpired,
  formatFrenchDateVerboseFromTimeStamp,
  getLastDayOfPreviousMonthFrenchFormatted,
  formatJsonSummaryAccountingDataToCsv,
  normaliseValue,
  formatDuration,
  getHumanReadableTimeElapsedFromDate,
  getNextExecution,
  formatNextExecution,
  ianaToWindowsMap
};
